import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { CalendarIcon, CheckCircle2, XCircle, Send, AlertTriangle, User, Clock, DoorOpen, Lock, Save, Pencil } from 'lucide-react';
import { fmtTimeIST } from '../lib/tz';


const DEPT_ORDER = ['Kitchen', 'Bar', 'Service', 'Housekeeping', 'Room Lobby'];

function ChecklistItems({ items, statuses, remarks, onToggle, onRemark, disabled }) {
  return (
    <CardContent className="p-0">
      {items.map((item, i) => {
        const val = statuses[item.id];
        const isNo = val === 'no';
        return (
          <div key={item.id} className={`px-4 py-3 ${i > 0 ? 'border-t border-[#E2E8DF]' : ''} ${isNo ? 'bg-[#C45A41]/5' : ''} ${disabled ? 'opacity-70' : ''}`}
            data-testid={`checklist-row-${item.id}`}>
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${isNo ? 'text-[#C45A41] font-medium' : 'text-[#1A2421]'}`}>{item.task_name}</p>
                {item.sub_area && <p className="text-[10px] text-[#5C6B64] mt-0.5">{item.area} / {item.sub_area}</p>}
                {item.is_critical && <AlertTriangle className="inline w-3 h-3 text-[#C45A41] ml-1" />}
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button onClick={() => !disabled && onToggle(item.id, 'yes')} disabled={disabled}
                  className={`px-3 py-1 rounded-md text-xs font-medium border transition-all ${val === 'yes'
                    ? 'bg-[#2D6A4F] text-white border-[#2D6A4F]'
                    : 'bg-white text-[#5C6B64] border-[#E2E8DF] hover:border-[#2D6A4F] hover:text-[#2D6A4F]'} ${disabled ? 'cursor-not-allowed' : ''}`}
                  data-testid={`yes-btn-${item.id}`}>YES</button>
                <button onClick={() => !disabled && onToggle(item.id, 'no')} disabled={disabled}
                  className={`px-3 py-1 rounded-md text-xs font-medium border transition-all ${val === 'no'
                    ? 'bg-[#C45A41] text-white border-[#C45A41]'
                    : 'bg-white text-[#5C6B64] border-[#E2E8DF] hover:border-[#C45A41] hover:text-[#C45A41]'} ${disabled ? 'cursor-not-allowed' : ''}`}
                  data-testid={`no-btn-${item.id}`}>NO</button>
              </div>
            </div>
            {isNo && (
              <Input placeholder="Describe the issue..." value={remarks[item.id] || ''}
                onChange={e => onRemark(item.id, e.target.value)}
                disabled={disabled}
                className="mt-2 h-8 text-xs bg-white border-[#C45A41]/30 focus:border-[#C45A41] text-[#C45A41]"
                data-testid={`remarks-${item.id}`} />
            )}
          </div>
        );
      })}
    </CardContent>
  );
}

export default function ManagerChecklist() {
  const { user, getHeaders, API } = useAuth();
  const [date, setDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 1); return d; });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [remarks, setRemarks] = useState({});
  const [existingEntry, setExistingEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState('');
  const [activeDept, setActiveDept] = useState('');
  // Room state
  const [allRooms, setAllRooms] = useState([]);
  const [completedRooms, setCompletedRooms] = useState([]);
  const [roomItems, setRoomItems] = useState([]);
  const [roomStatuses, setRoomStatuses] = useState({});
  const [roomRemarks, setRoomRemarks] = useState({});
  const [selectedRoom, setSelectedRoom] = useState('');
  const [roomExisting, setRoomExisting] = useState(null);
  const [roomLoading, setRoomLoading] = useState(false);
  const [hasRoomItems, setHasRoomItems] = useState(false);
  const [roomLocked, setRoomLocked] = useState(false);
  // Edit mode
  const [editingDept, setEditingDept] = useState(null);
  // Confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: '', dept: '' });

  const dateStr = format(date, 'yyyy-MM-dd');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/checklist/my-items?date=${dateStr}`, { headers: getHeaders() });
      const loadedItems = res.data.items || [];
      setItems(loadedItems);
      setAllRooms(res.data.all_rooms || []);
      setCompletedRooms(res.data.completed_rooms || []);
      setHasRoomItems((res.data.room_items_count || 0) > 0);
      const entry = res.data.existing_entry;
      if (entry) {
        setExistingEntry(entry);
        const s = {}, r = {};
        const deptSubs = entry.department_submissions || {};
        for (const [, ds] of Object.entries(deptSubs)) {
          for (const ei of ds.items || []) {
            s[ei.checklist_item_id] = ei.status === 'done' ? 'yes' : ei.status;
            r[ei.checklist_item_id] = ei.remarks || '';
          }
        }
        if (Object.keys(s).length === 0) {
          for (const ei of entry.items || []) {
            s[ei.checklist_item_id] = ei.status === 'done' ? 'yes' : ei.status;
            r[ei.checklist_item_id] = ei.remarks || '';
          }
        }
        setStatuses(s);
        setRemarks(r);
      } else {
        setExistingEntry(null);
        setStatuses({});
        setRemarks({});
      }
      const depts = [...new Set(loadedItems.map(i => i.department))];
      const ordered = DEPT_ORDER.filter(d => depts.includes(d));
      if (ordered.length > 0 && !activeDept) setActiveDept(ordered[0]);
    } catch (err) { console.error('Failed to load checklist items:', err); toast.error('Failed to load checklist'); }
    finally { setLoading(false); }
  }, [API, getHeaders, dateStr, activeDept]);

  useEffect(() => { load(); }, [load]);

  const loadRoomItems = useCallback(async (roomNum) => {
    setRoomLoading(true);
    try {
      const res = await axios.get(`${API}/checklist/room-items?date=${dateStr}&room_number=${roomNum}`, { headers: getHeaders() });
      setRoomItems(res.data.items || []);
      const entry = res.data.existing_entry;
      setRoomExisting(entry);
      if (entry) {
        setRoomLocked(entry.locked === true);
        const s = {}, r = {};
        for (const ei of entry.items || []) {
          s[ei.checklist_item_id] = ei.status;
          r[ei.checklist_item_id] = ei.remarks || '';
        }
        setRoomStatuses(s);
        setRoomRemarks(r);
      } else {
        setRoomLocked(false);
        setRoomStatuses({});
        setRoomRemarks({});
      }
    } catch (err) { console.error('Failed to load room checklist:', err); toast.error('Failed to load room checklist'); }
    finally { setRoomLoading(false); }
  }, [API, getHeaders, dateStr]);

  useEffect(() => {
    if (selectedRoom) loadRoomItems(selectedRoom);
  }, [selectedRoom, loadRoomItems]);

  // Get department submission status
  const getDeptStatus = (dept) => {
    const ds = existingEntry?.department_submissions?.[dept];
    if (!ds) return { status: 'new', locked: false, editCount: 0 };
    return {
      status: ds.status || 'new', // 'saved', 'submitted', 'completed'
      locked: ds.locked === true,
      editCount: ds.edit_count || 0,
      submittedBy: ds.submitted_by_name || '',
      submittedTime: ds.submitted_time_local || '',
      lastEditedBy: ds.last_edited_by_name || '',
      lastEditTime: ds.last_edit_time || ''
    };
  };

  const isDeptLocked = (dept) => {
    if (editingDept === dept) return false;
    return getDeptStatus(dept).locked;
  };

  // Save department (no lock)
  const handleSaveDept = async (dept) => {
    const deptItems = items.filter(i => i.department === dept);
    setSubmitting(dept + '_save');
    try {
      const res = await axios.post(`${API}/checklist/submit-department`, {
        date: dateStr, department: dept, submit: false,
        items: deptItems.map(i => ({ checklist_item_id: i.id, status: statuses[i.id] || 'no', remarks: remarks[i.id] || '' }))
      }, { headers: getHeaders() });
      setExistingEntry(res.data);
      toast.success(`${dept} checklist saved`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to save';
      if (err.response?.status === 409) { toast.info(msg); await load(); }
      else { toast.error(msg); }
    }
    finally { setSubmitting(''); }
  };

  // Submit department (lock) - called after confirmation
  const handleFinalSubmitDept = async (dept) => {
    const deptItems = items.filter(i => i.department === dept);
    const unmarked = deptItems.filter(i => !statuses[i.id] || (statuses[i.id] !== 'yes' && statuses[i.id] !== 'no'));
    if (unmarked.length > 0) {
      toast.error(`Mark all items as YES or NO before submitting ${dept}`);
      return;
    }
    setSubmitting(dept + '_submit');
    try {
      const res = await axios.post(`${API}/checklist/submit-department`, {
        date: dateStr, department: dept, submit: true,
        items: deptItems.map(i => ({ checklist_item_id: i.id, status: statuses[i.id] || 'no', remarks: remarks[i.id] || '' }))
      }, { headers: getHeaders() });
      setExistingEntry(res.data);
      setEditingDept(null);
      toast.success(`${dept} checklist submitted & locked`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to submit';
      if (err.response?.status === 409) { toast.info(msg); await load(); }
      else { toast.error(msg); }
    }
    finally { setSubmitting(''); }
  };

  // Edit submitted department - called after confirmation
  const handleEditSaveDept = async (dept) => {
    const deptItems = items.filter(i => i.department === dept);
    const unmarked = deptItems.filter(i => !statuses[i.id] || (statuses[i.id] !== 'yes' && statuses[i.id] !== 'no'));
    if (unmarked.length > 0) {
      toast.error(`Mark all items as YES or NO before saving edit`);
      return;
    }
    setSubmitting(dept + '_edit');
    try {
      const res = await axios.post(`${API}/checklist/edit-department`, {
        date: dateStr, department: dept,
        items: deptItems.map(i => ({ checklist_item_id: i.id, status: statuses[i.id] || 'no', remarks: remarks[i.id] || '' }))
      }, { headers: getHeaders() });
      setExistingEntry(res.data);
      setEditingDept(null);
      toast.success(`${dept} checklist updated`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to update';
      if (err.response?.status === 409) { toast.info(msg); await load(); setEditingDept(null); }
      else { toast.error(msg); }
    }
    finally { setSubmitting(''); }
  };

  // Confirmation handlers
  const handleSubmitClick = (dept) => {
    const deptItems = items.filter(i => i.department === dept);
    const unmarked = deptItems.filter(i => !statuses[i.id] || (statuses[i.id] !== 'yes' && statuses[i.id] !== 'no'));
    if (unmarked.length > 0) {
      toast.error(`Mark all items as YES or NO before submitting ${dept}`);
      return;
    }
    setConfirmDialog({ open: true, type: 'submit', dept });
  };

  const handleEditClick = (dept) => {
    const ds = getDeptStatus(dept);
    setConfirmDialog({ open: true, type: 'edit', dept, editCount: ds.editCount });
  };

  const handleConfirm = () => {
    const { type, dept } = confirmDialog;
    setConfirmDialog({ open: false, type: '', dept: '' });
    if (type === 'submit') {
      handleFinalSubmitDept(dept);
    } else if (type === 'edit') {
      setEditingDept(dept);
    }
  };

  const handleSubmitRoom = async () => {
    const unmarked = roomItems.filter(i => !roomStatuses[i.id] || (roomStatuses[i.id] !== 'yes' && roomStatuses[i.id] !== 'no'));
    if (unmarked.length > 0) {
      toast.error('Mark all items as YES or NO');
      return;
    }
    setSubmitting('room');
    try {
      const res = await axios.post(`${API}/checklist/submit-room`, {
        date: dateStr, room_number: selectedRoom,
        items: roomItems.map(i => ({ checklist_item_id: i.id, status: roomStatuses[i.id] || 'no', remarks: roomRemarks[i.id] || '' }))
      }, { headers: getHeaders() });
      setRoomExisting(res.data);
      toast.success(`Room ${selectedRoom} checklist submitted`);
      const myRes = await axios.get(`${API}/checklist/my-items?date=${dateStr}`, { headers: getHeaders() });
      setCompletedRooms(myRes.data.completed_rooms || []);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to submit';
      if (err.response?.status === 409) { toast.info(msg); setRoomLocked(true); }
      else { toast.error(msg); }
    }
    finally { setSubmitting(''); }
  };

  const orderedDepts = DEPT_ORDER.filter(d => [...new Set(items.map(i => i.department))].includes(d));
  const deptItems = items.filter(i => i.department === activeDept);
  const deptSub = existingEntry?.department_submissions?.[activeDept];
  const activeDeptStatus = getDeptStatus(activeDept);
  const activeDeptLocked = isDeptLocked(activeDept);

  const deptYes = deptItems.filter(i => statuses[i.id] === 'yes').length;
  const deptNo = deptItems.filter(i => statuses[i.id] === 'no').length;
  const deptTotal = deptItems.length;
  const deptPct = deptTotal > 0 ? Math.round(deptYes / deptTotal * 100) : 0;

  const totalYes = items.filter(i => statuses[i.id] === 'yes').length;
  const totalItems = items.length;
  const overallPct = totalItems > 0 ? Math.round(totalYes / totalItems * 100) : 0;

  const isRoomTab = activeDept === '__rooms__';

  // Render the action buttons for a department
  const renderDeptActions = () => {
    const isEditing = editingDept === activeDept;

    // Editing mode: show Save Edit button
    if (isEditing) {
      return (
        <div className="space-y-2 mb-4">
          <Button onClick={() => handleEditSaveDept(activeDept)} disabled={!!submitting}
            className="w-full h-10 bg-[#D4A017] hover:bg-[#B8901A] text-white text-sm shadow-sm"
            data-testid={`save-edit-dept-${activeDept}`}>
            <Save className="w-4 h-4 mr-2" />
            {submitting === activeDept + '_edit' ? 'Saving...' : `Save Edit (${activeDeptStatus.editCount + 1}/2)`}
          </Button>
          <Button variant="outline" onClick={() => { setEditingDept(null); load(); }}
            className="w-full h-10 border-[#E2E8DF] text-[#5C6B64]"
            data-testid={`cancel-edit-dept-${activeDept}`}>
            Cancel Edit
          </Button>
        </div>
      );
    }

    // Submitted & locked: show locked status + edit button
    if (activeDeptStatus.locked) {
      return (
        <div className="mb-4 space-y-2">
          <div className="w-full p-3 bg-[#F3F5F2] border border-[#E2E8DF] rounded-lg"
            data-testid={`locked-dept-${activeDept}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#2D6A4F]" />
                <span className="text-sm text-[#2D6A4F] font-medium">Submitted</span>
              </div>
              <div className="flex items-center gap-2">
                {activeDeptStatus.editCount > 0 && (
                  <span className="text-[10px] text-[#D4A017] bg-[#D4A017]/10 px-1.5 py-0.5 rounded">
                    Edited {activeDeptStatus.editCount}/2
                  </span>
                )}
                {activeDeptStatus.editCount < 2 && (
                  <Button variant="outline" size="sm" onClick={() => handleEditClick(activeDept)}
                    className="h-7 text-xs border-[#E2E8DF] text-[#5C6B64] hover:text-[color:var(--color-primary)] hover:border-[color:var(--color-primary)]"
                    data-testid={`edit-dept-${activeDept}`}>
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                )}
                {activeDeptStatus.editCount >= 2 && (
                  <span className="text-[10px] text-[#C45A41]">Max edits reached</span>
                )}
              </div>
            </div>
            {activeDeptStatus.lastEditTime && (
              <p className="text-[10px] text-[#5C6B64] mt-1">
                Last edited by {activeDeptStatus.lastEditedBy} at {fmtTimeIST(activeDeptStatus.lastEditTime)}
              </p>
            )}
          </div>
        </div>
      );
    }

    // Not submitted: show Save + Submit buttons
    return (
      <div className="space-y-2 mb-4">
        <Button onClick={() => handleSaveDept(activeDept)} disabled={!!submitting}
          variant="outline"
          className="w-full h-10 border-[#E2E8DF] text-[color:var(--color-primary)] hover:bg-[#F3F5F2] text-sm"
          data-testid={`save-dept-${activeDept}`}>
          <Save className="w-4 h-4 mr-2" />
          {submitting === activeDept + '_save' ? 'Saving...' : `Save ${activeDept}`}
        </Button>
        <Button onClick={() => handleSubmitClick(activeDept)} disabled={!!submitting}
          className="w-full h-10 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white text-sm shadow-sm"
          data-testid={`submit-dept-${activeDept}`}>
          <Send className="w-4 h-4 mr-2" />
          {submitting === activeDept + '_submit' ? 'Submitting...' : `Submit ${activeDept} Checklist`}
        </Button>
      </div>
    );
  };

  return (
    <div className="w-full" data-testid="manager-checklist-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold tracking-tight text-[#1A2421]">Opening Checklist</h2>
          <p className="text-sm text-[#5C6B64] mt-1">{user?.outlet_name || 'Your Outlet'}</p>
        </div>
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="border-[#E2E8DF] text-[#1A2421]" data-testid="checklist-date-picker">
              <CalendarIcon className="w-4 h-4 mr-2 text-[#5C6B64]" />
              {format(date, 'dd MMM yyyy')}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar mode="single" selected={date} onSelect={d => { if (d) { setDate(d); setCalendarOpen(false); setActiveDept(''); setSelectedRoom(''); setEditingDept(null); } }} disabled={d => d > new Date()} />
          </PopoverContent>
        </Popover>
      </div>

      {/* Overall progress (excludes Room) */}
      <div className="flex items-center gap-4 mb-4 p-3 bg-[#F3F5F2] rounded-lg border border-[#E2E8DF]">
        <div className="flex-1">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-[#5C6B64]">Overall (excl. Rooms)</span>
            <span className="font-semibold text-[#1A2421]" data-testid="checklist-progress">{totalYes}/{totalItems} ({overallPct}%)</span>
          </div>
          <div className="w-full h-2 bg-[#E2E8DF] rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-300"
              style={{ width: `${overallPct}%`, backgroundColor: overallPct === 100 ? '#2D6A4F' : overallPct >= 70 ? '#D4A017' : '#C45A41' }} />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="animate-spin w-8 h-8 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" /></div>
      ) : items.length === 0 && !hasRoomItems ? (
        <Card className="border-[#E2E8DF]"><CardContent className="py-10 text-center text-[#5C6B64]">No checklist items assigned to your outlet</CardContent></Card>
      ) : (
        <>
          {/* Department tabs + Room tab */}
          <div className="flex flex-wrap gap-2 mb-4" data-testid="dept-tabs">
            {orderedDepts.map(dept => {
              const ds = getDeptStatus(dept);
              const isSubmitted = ds.locked;
              const isSaved = ds.status === 'saved';
              const isActive = activeDept === dept;
              return (
                <Button key={dept} variant={isActive ? 'default' : 'outline'} size="sm"
                  onClick={() => { setActiveDept(dept); setEditingDept(null); }}
                  className={`relative ${isActive ? 'bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)]' : 'border-[#E2E8DF] text-[#5C6B64] hover:bg-[#F3F5F2]'}`}
                  data-testid={`dept-tab-${dept}`}>
                  {dept}
                  {isSubmitted && <Lock className="w-3 h-3 ml-1.5 text-[#2D6A4F] bg-white rounded-full p-[1px]" />}
                  {isSaved && !isSubmitted && <Save className="w-3 h-3 ml-1.5 text-[#D4A017]" />}
                </Button>
              );
            })}
            {hasRoomItems && (
              <Button variant={isRoomTab ? 'default' : 'outline'} size="sm"
                onClick={() => { setActiveDept('__rooms__'); setEditingDept(null); }}
                className={`relative ${isRoomTab ? 'bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)]' : 'border-[#E2E8DF] text-[#5C6B64] hover:bg-[#F3F5F2]'}`}
                data-testid="dept-tab-rooms">
                <DoorOpen className="w-3.5 h-3.5 mr-1" /> Rooms
                {completedRooms.length > 0 && (
                  <Badge className="ml-1.5 bg-[#2D6A4F]/20 text-[#2D6A4F] text-[10px] px-1">{completedRooms.length}/{allRooms.length}</Badge>
                )}
              </Button>
            )}
          </div>

          {/* Standard department view */}
          {!isRoomTab && activeDept && (
            <>
              <Card className={`border-[#E2E8DF] shadow-sm mb-4 ${activeDeptStatus.locked && editingDept !== activeDept ? 'border-l-4 border-l-[#2D6A4F]' : activeDeptStatus.status === 'saved' ? 'border-l-4 border-l-[#D4A017]' : ''}`}>
                <CardHeader className="py-3 px-4 bg-[#F9F9F8] border-b border-[#E2E8DF]">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <CardTitle className="text-base text-[#1A2421]">{activeDept}</CardTitle>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-[#2D6A4F]/10 text-[#2D6A4F] border-[#2D6A4F]/20 text-xs">{deptYes} YES</Badge>
                        {deptNo > 0 && <Badge className="bg-[#C45A41]/10 text-[#C45A41] border-[#C45A41]/20 text-xs">{deptNo} NO</Badge>}
                        <span className="text-xs text-[#5C6B64]">{deptPct}%</span>
                      </div>
                    </div>
                    {/* Status badge */}
                    <div className="flex items-center gap-2">
                      {activeDeptStatus.locked && (
                        <Badge className="bg-[#2D6A4F]/10 text-[#2D6A4F] border-[#2D6A4F]/20 text-xs" data-testid={`status-badge-${activeDept}`}>
                          <Lock className="w-3 h-3 mr-1" /> Submitted
                        </Badge>
                      )}
                      {activeDeptStatus.status === 'saved' && !activeDeptStatus.locked && (
                        <Badge className="bg-[#D4A017]/10 text-[#D4A017] border-[#D4A017]/20 text-xs" data-testid={`status-badge-${activeDept}`}>
                          <Save className="w-3 h-3 mr-1" /> Saved
                        </Badge>
                      )}
                    </div>
                  </div>
                  {/* Submission info */}
                  {deptSub && (activeDeptStatus.locked || activeDeptStatus.status === 'saved') && (
                    <div className="flex items-center gap-2 text-xs text-[#5C6B64] mt-2">
                      <User className="w-3 h-3" /><span>{deptSub.submitted_by_name}</span>
                      <Clock className="w-3 h-3 ml-1" /><span>{deptSub.submitted_time_local}</span>
                      {deptSub.is_on_time !== undefined && (
                        <Badge className={deptSub.is_on_time
                          ? 'bg-[#2D6A4F]/10 text-[#2D6A4F] border-[#2D6A4F]/20 text-[10px]'
                          : 'bg-[#C45A41]/10 text-[#C45A41] border-[#C45A41]/20 text-[10px]'}>
                          {deptSub.is_on_time ? 'On Time' : 'Late'}
                        </Badge>
                      )}
                    </div>
                  )}
                </CardHeader>
                <ChecklistItems items={deptItems} statuses={statuses} remarks={remarks}
                  onToggle={(id, val) => setStatuses(p => ({ ...p, [id]: val }))}
                  onRemark={(id, val) => setRemarks(p => ({ ...p, [id]: val }))}
                  disabled={activeDeptLocked} />
              </Card>
              {renderDeptActions()}
            </>
          )}

          {/* Room checklist view */}
          {isRoomTab && (
            <>
              <div className="flex items-center gap-3 mb-4">
                <Select value={selectedRoom} onValueChange={setSelectedRoom}>
                  <SelectTrigger className="w-48 h-10 border-[#E2E8DF]" data-testid="room-select">
                    <SelectValue placeholder="Select Room" />
                  </SelectTrigger>
                  <SelectContent>
                    {allRooms.map(r => {
                      const done = completedRooms.find(c => c.room_number === r);
                      return (
                        <SelectItem key={r} value={r} data-testid={`room-option-${r}`}>
                          Room {r} {done ? `(${done.adherence_score}%)` : ''}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {selectedRoom && (
                roomLoading ? (
                  <div className="flex items-center justify-center py-10"><div className="animate-spin w-6 h-6 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" /></div>
                ) : (
                  <>
                    <Card className="border-[#E2E8DF] shadow-sm mb-4">
                      <CardHeader className="py-3 px-4 bg-[#F9F9F8] border-b border-[#E2E8DF]">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <CardTitle className="text-base text-[#1A2421]">Room {selectedRoom}</CardTitle>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-[#2D6A4F]/10 text-[#2D6A4F] border-[#2D6A4F]/20 text-xs">
                              {roomItems.filter(i => roomStatuses[i.id] === 'yes').length} YES
                            </Badge>
                            {roomItems.filter(i => roomStatuses[i.id] === 'no').length > 0 && (
                              <Badge className="bg-[#C45A41]/10 text-[#C45A41] border-[#C45A41]/20 text-xs">
                                {roomItems.filter(i => roomStatuses[i.id] === 'no').length} NO
                              </Badge>
                            )}
                          </div>
                          {roomExisting && (
                            <div className="flex items-center gap-2 text-xs text-[#5C6B64]">
                              <User className="w-3 h-3" /><span>{roomExisting.submitted_by_name}</span>
                              <Clock className="w-3 h-3 ml-1" /><span>{roomExisting.submitted_time_local}</span>
                            </div>
                          )}
                        </div>
                      </CardHeader>
                      <ChecklistItems items={roomItems} statuses={roomStatuses} remarks={roomRemarks}
                        onToggle={(id, val) => setRoomStatuses(p => ({ ...p, [id]: val }))}
                        onRemark={(id, val) => setRoomRemarks(p => ({ ...p, [id]: val }))}
                        disabled={roomLocked} />
                    </Card>
                    {roomLocked ? (
                      <div className="w-full h-12 flex items-center justify-center bg-[#F3F5F2] border border-[#E2E8DF] rounded-lg mb-4 text-sm text-[#5C6B64]"
                        data-testid="locked-room-btn">
                        <Lock className="w-4 h-4 mr-2 text-[#2D6A4F]" /> Room {selectedRoom} checklist is submitted for the day
                      </div>
                    ) : (
                      <Button onClick={handleSubmitRoom} disabled={!!submitting}
                        className="w-full h-12 bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white text-base shadow-lg mb-4"
                        data-testid="submit-room-btn">
                        <Send className="w-4 h-4 mr-2" />
                        {submitting === 'room' ? 'Submitting...' : roomExisting ? `Update Room ${selectedRoom}` : `Submit Room ${selectedRoom}`}
                      </Button>
                    )}
                  </>
                )
              )}

              {/* Completed rooms overview */}
              {completedRooms.length > 0 && (
                <Card className="border-[#E2E8DF] shadow-sm">
                  <CardHeader className="py-3 px-4">
                    <CardTitle className="text-sm text-[#5C6B64]">Completed Rooms ({completedRooms.length}/{allRooms.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {completedRooms.map((cr, i) => (
                      <div key={cr.room_number} className={`flex items-center justify-between px-4 py-2 ${i > 0 ? 'border-t border-[#E2E8DF]' : ''}`}
                        data-testid={`completed-room-${cr.room_number}`}>
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-[#2D6A4F]" />
                          <span className="text-sm text-[#1A2421]">Room {cr.room_number}</span>
                          {cr.locked && <Lock className="w-3 h-3 text-[#5C6B64]" />}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-xs font-semibold ${cr.adherence_score >= 80 ? 'text-[#2D6A4F]' : cr.adherence_score >= 50 ? 'text-[#D4A017]' : 'text-[#C45A41]'}`}>
                            {cr.adherence_score}%
                          </span>
                          <span className="text-[10px] text-[#5C6B64]">{cr.submitted_by_name}</span>
                          <span className="text-[10px] text-[#5C6B64]">{cr.submitted_time}</span>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Department status overview */}
          {!isRoomTab && (
            <Card className="border-[#E2E8DF] shadow-sm">
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm text-[#5C6B64]">Department Status</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {orderedDepts.map((dept, i) => {
                  const ds = getDeptStatus(dept);
                  const dItems = items.filter(it => it.department === dept);
                  const dYes = dItems.filter(it => statuses[it.id] === 'yes').length;
                  const dsub = existingEntry?.department_submissions?.[dept];
                  return (
                    <div key={dept} className={`flex items-center justify-between px-4 py-2.5 ${i > 0 ? 'border-t border-[#E2E8DF]' : ''}`}
                      data-testid={`dept-status-${dept}`}>
                      <div className="flex items-center gap-2">
                        {ds.locked ? <Lock className="w-4 h-4 text-[#2D6A4F]" /> :
                         ds.status === 'saved' ? <Save className="w-4 h-4 text-[#D4A017]" /> :
                         <XCircle className="w-4 h-4 text-[#C5CBC8]" />}
                        <span className="text-sm text-[#1A2421]">{dept}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#5C6B64]">{dYes}/{dItems.length}</span>
                        {dsub?.submitted_by_name && (
                          <span className="text-[10px] text-[#5C6B64]">by {dsub.submitted_by_name}</span>
                        )}
                        {ds.editCount > 0 && (
                          <span className="text-[9px] text-[#D4A017] bg-[#D4A017]/10 px-1 rounded">
                            Edited {ds.editCount}x
                          </span>
                        )}
                        <Badge className={
                          ds.locked ? 'bg-[#2D6A4F]/10 text-[#2D6A4F] border-[#2D6A4F]/20 text-[10px]' :
                          ds.status === 'saved' ? 'bg-[#D4A017]/10 text-[#D4A017] border-[#D4A017]/20 text-[10px]' :
                          'bg-[#5C6B64]/10 text-[#5C6B64] border-[#5C6B64]/20 text-[10px]'
                        }>
                          {ds.locked ? 'Submitted' : ds.status === 'saved' ? 'Saved' : 'Pending'}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(o) => !o && setConfirmDialog({ open: false, type: '', dept: '' })}>
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#1A2421]">
              {confirmDialog.type === 'submit' ? 'Submit Checklist?' : 'Edit Submitted Checklist?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#5C6B64]">
              {confirmDialog.type === 'submit'
                ? `Are you sure you want to submit the ${confirmDialog.dept} checklist? Once submitted, this section will be locked for the day.`
                : `You are editing submitted data. You can only edit this section ${2 - (confirmDialog.editCount || 0)} more time(s) today. Continue?`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E2E8DF] text-[#5C6B64]" data-testid="confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}
              className={confirmDialog.type === 'submit' ? 'bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white' : 'bg-[#D4A017] hover:bg-[#B8901A] text-white'}
              data-testid="confirm-action">
              {confirmDialog.type === 'submit' ? 'Submit & Lock' : 'Continue Editing'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
